import {
  APPLICATION_STATUS_OPTIONS,
  IS_SEEN_OPTIONS,
  NOTICE_PERIOD_OPTIONS,
} from 'src/constants';
import type { Location } from 'src/openapi';
import { currentFilterInitialState } from 'src/redux/slices/applicantReview';
import type { InputFilters, LocationOption, MinMax, Option } from '../types';

/**
 * Applicant review's filters as Abekus AI sets them (open_applicant_review in
 * the backend's employer tools): the page's own values, without the labels
 * and lookups the page adds. Experience is in months, as the page's options
 * hold it.
 */
export interface AssistantApplicantFilters {
  applicant_status?: string[];
  is_applicant_seen?: 'true' | 'false';
  user_search_query?: string;
  matching_skills?: string[];
  missing_skills?: string[];
  experience?: Range;
  internship_experience?: Range;
  matching_location?: Location;
  notice_period?: string;
  cgpa?: Range;
  passing_year?: Range;
  match_percentile?: Range;
  assessment_score?: Range;
  profile_rating?: Range;
  applicant_tag?: string[];
  colleges?: { id: string; name: string }[];
  job_roles?: { id: string; name: string }[];
  sort_by?: string;
  sort_order?: string;
}

interface Range {
  min?: number | null;
  max?: number | null;
}

const MONTHS = 12;

const option = (value: number, label = String(value)): Option<number> => ({
  label,
  value,
});

const range = (r?: Range, toLabel?: (v: number) => string): MinMax<number> => ({
  min: r?.min != null ? option(r.min, toLabel?.(r.min)) : null,
  max: r?.max != null ? option(r.max, toLabel?.(r.max)) : null,
});

const named = (names?: string[]) =>
  names?.length ? names.map((name) => ({ label: name, value: name })) : null;

const listed = (rows?: { id: string; name: string }[]) =>
  rows?.length ? rows.map((row) => ({ label: row.name, value: row.id })) : null;

/** A full filter set for the page: what isn't named is the page's default. */
export const filtersFromAssistant = (
  spec: AssistantApplicantFilters,
): InputFilters => {
  const base = currentFilterInitialState;
  const location: LocationOption | null = spec.matching_location?.id
    ? {
        label:
          spec.matching_location.terms?.[0] ??
          spec.matching_location.description ??
          '',
        value: spec.matching_location.id,
        location_details: spec.matching_location,
      }
    : null;
  return {
    ...base,
    applicant_status: spec.applicant_status
      ? APPLICATION_STATUS_OPTIONS.filter((o) =>
          spec.applicant_status!.includes(o.value),
        )
      : base.applicant_status,
    is_applicant_seen:
      IS_SEEN_OPTIONS.find((o) => o.value === spec.is_applicant_seen) ??
      base.is_applicant_seen,
    user_search_query: spec.user_search_query ?? '',
    matching_skills: named(spec.matching_skills),
    missing_skills: named(spec.missing_skills),
    experience: range(spec.experience, (months) => String(months / MONTHS)),
    internship_experience: range(spec.internship_experience),
    matching_locations: location ? [location] : null,
    notice_period:
      NOTICE_PERIOD_OPTIONS.find((o) => o.value === spec.notice_period) ??
      base.notice_period,
    cgpa: range(spec.cgpa),
    passing_year: range(spec.passing_year),
    match_percentile: range(spec.match_percentile),
    assessment_score: range(spec.assessment_score),
    profile_rating: range(spec.profile_rating),
    applicant_tag: named(spec.applicant_tag) ?? [],
    colleges: listed(spec.colleges),
    job_roles: listed(spec.job_roles),
    sort_by: spec.sort_by ?? base.sort_by,
    sort_order: spec.sort_order ?? base.sort_order,
  };
};

// The assistant's words for the page's stages and sorts (APPLICANT_FILTERS).
const STAGE_WORDS: Record<string, string> = {
  selected: 'hired',
  hold: 'on_hold',
};
const SORT_WORDS: Record<string, string> = {
  'date:desc': 'newest',
  'date:asc': 'oldest',
  'percentile:desc': 'best_match',
  'resume_analysis_overall_score:desc': 'ai_fit',
  'experience:desc': 'most_experienced',
};

const bounds = (key: string, r: MinMax<number>, scale = 1) => ({
  ...(r.min && { [`min_${key}`]: r.min.value / scale }),
  ...(r.max && { [`max_${key}`]: r.max.value / scale }),
});

const sameValues = (a: Option[], b: Option[]) =>
  a.length === b.length && a.every((o) => b.some((p) => p.value === o.value));

/**
 * The filters on screen in the assistant's own words, what it leaves out
 * being the page's default: sent with each question from the page so a change
 * ("now only the new ones") keeps the rest.
 */
export const describeFiltersForAssistant = (
  filters: InputFilters,
  jobType?: string,
): Record<string, unknown> => {
  const words: Record<string, unknown> = {};
  if (
    !sameValues(
      filters.applicant_status,
      currentFilterInitialState.applicant_status,
    )
  )
    words.stages = filters.applicant_status.map(
      (o) => STAGE_WORDS[o.value] ?? o.value,
    );
  if (filters.is_applicant_seen.value === 'false') words.unseen_only = true;
  if (filters.is_applicant_seen.value === 'true') words.seen_only = true;
  if (filters.user_search_query) words.name = filters.user_search_query;
  if (filters.matching_skills?.length)
    words.skills = filters.matching_skills.map((o) => o.label);
  if (filters.missing_skills?.length)
    words.missing_skills = filters.missing_skills.map((o) => o.label);
  Object.assign(
    words,
    jobType === 'Internship'
      ? bounds('internship_months', filters.internship_experience)
      : bounds('experience_years', filters.experience, MONTHS),
  );
  const place = filters.matching_locations?.[0];
  if (place?.location_details?.id) words.location = place.label;
  if (filters.notice_period.value !== 'all')
    words.max_notice_period_days = Number(filters.notice_period.value);
  Object.assign(
    words,
    bounds('cgpa', filters.cgpa),
    bounds('passing_year', filters.passing_year),
    bounds('match_percent', filters.match_percentile),
    bounds('assessment_score', filters.assessment_score),
    bounds('rating', filters.profile_rating),
  );
  if (filters.applicant_tag.length)
    words.tags = filters.applicant_tag.map((o) => o.label);
  if (filters.colleges?.length)
    words.colleges = filters.colleges.map((o) => o.label);
  if (filters.job_roles?.length)
    words.current_roles = filters.job_roles.map((o) => o.label);
  const sort = SORT_WORDS[`${filters.sort_by}:${filters.sort_order}`];
  if (sort && sort !== 'newest') words.sort = sort;
  // Set on the page only, beyond what the assistant can set: it says so
  // rather than clearing them unasked.
  const pageOnly = [
    filters.skill_score?.length && 'skill scores',
    filters.assessment_screening?.length && 'screening answers',
  ].filter(Boolean);
  if (pageOnly.length) words.also_on_page = pageOnly;
  return words;
};
